import { zodResolver } from '@hookform/resolvers/zod'
import { Clock } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Input } from '@/components/ui/Input'
import { useSaveShift, type ShiftInput } from '../api'
import type { Shift } from '../types'
import { FormDialog } from './FormDialog'

const optionalTime = z
  .string()
  .refine((v) => v === '' || /^([01]\d|2[0-3]):[0-5]\d$/.test(v), 'Use the HH:MM format.')
  .transform((v) => (v === '' ? null : v))

const shiftSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter the shift name.').max(50, 'Use at most 50 characters.'),
    startTime: optionalTime,
    endTime: optionalTime,
  })
  .refine((v) => !v.startTime || !v.endTime || v.endTime > v.startTime, {
    path: ['endTime'],
    message: 'The end time must be after the start time.',
  })

type ShiftFormInput = z.input<typeof shiftSchema>
const FIELDS = ['name', 'startTime', 'endTime'] as const
const FORM_ID = 'shift-form'

interface ShiftDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shift?: Shift | null
}

export function ShiftDialog({ open, onOpenChange, shift }: ShiftDialogProps) {
  const { t } = useTranslation('academic')
  const save = useSaveShift()
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Clock />}
      title={shift ? t('Edit {{name}}', { name: shift.name }) : t('New shift')}
      description={t('Sections can be assigned to a shift, e.g. morning or afternoon.')}
      formId={FORM_ID}
      submitLabel={shift ? t('Save changes') : t('Create shift')}
      pending={save.isPending}
      size="sm"
    >
      <ShiftForm
        defaults={{
          name: shift?.name ?? '',
          startTime: shift?.startTime ?? '',
          endTime: shift?.endTime ?? '',
        }}
        onSubmit={async (values) => {
          const saved = await save.mutateAsync({ ...values, id: shift?.id })
          toast.success(shift ? t('Shift updated') : t('Shift created'), saved.name)
          onOpenChange(false)
        }}
      />
    </FormDialog>
  )
}

function ShiftForm({
  defaults,
  onSubmit,
}: {
  defaults: ShiftFormInput
  onSubmit: (values: ShiftInput) => Promise<void>
}) {
  const { t } = useTranslation('academic')
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ShiftFormInput, unknown, ShiftInput>({
    resolver: zodResolver(shiftSchema),
    defaultValues: defaults,
  })

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <form id={FORM_ID} onSubmit={(e) => void submit(e)} noValidate className="grid grid-cols-2 gap-4">
      <FormField label={t('Name')} error={errors.name?.message} required className="col-span-2">
        <Input placeholder={t('Morning')} autoComplete="off" {...register('name')} />
      </FormField>
      <FormField label={t('Starts at')} error={errors.startTime?.message}>
        <Input type="time" {...register('startTime')} />
      </FormField>
      <FormField label={t('Ends at')} error={errors.endTime?.message}>
        <Input type="time" {...register('endTime')} />
      </FormField>
    </form>
  )
}
